import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CalendarDays, Clock, MessageCircle, ArrowRight, Mic, CalendarPlus, Scissors, Stethoscope, Loader2, Search, ClipboardCheck, UserPlus, CheckCircle2 } from "lucide-react";
import WhatsAppReminders from "../components/agenda/WhatsAppReminders";
import IntakeDialog from "../components/agenda/IntakeDialog";
import { toast } from "sonner";
import GoogleCalendarCard from "../components/GoogleCalendarCard";
import SurgeryNotes from "../components/patient/SurgeryNotes";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const TYPE_LABEL = { primera_vez: "Primera vez", seguimiento: "Seguimiento", vacunacion: "Vacunación", cirugia: "Cirugía" };
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const STATUS = {
  programada: { l: "Programada", cls: "bg-brand-sage/15 text-brand-sageDark" },
  completada: { l: "Completada", cls: "bg-brand-ink/10 text-brand-ink" },
  cancelada: { l: "Cancelada", cls: "bg-red-100 text-red-600" },
};
const waDigits = (p) => { if (!p) return null; let d = (p + "").replace(/\D/g, ""); if (!d) return null; if (d.length === 10) d = "52" + d; return d; };

export default function Agenda() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [gEvents, setGEvents] = useState([]);
  const [gError, setGError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("proximas"); // proximas | todas
  const [newType, setNewType] = useState(null); // null | 'seguimiento' | 'cirugia'
  const [patients, setPatients] = useState([]);
  const [pQuery, setPQuery] = useState("");
  const [apptForm, setApptForm] = useState({ patient_id: "", scheduled_at: toLocalInput(), reason: "", email: "" });
  const [savingAppt, setSavingAppt] = useState(false);
  const [surgeryFor, setSurgeryFor] = useState(null); // appointment object (cirugia recién creada)
  const [newMode, setNewMode] = useState("existing"); // existing | new (primera vez, sin expediente)
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [intakeFor, setIntakeFor] = useState(null);

  const openNew = (type) => {
    setNewType(type);
    setApptForm({ patient_id: "", scheduled_at: toLocalInput(), reason: "", email: "" });
    setPQuery(""); setNewMode("existing"); setNewName(""); setNewPhone("");
    if (patients.length === 0) api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
  };

  const saveNew = async () => {
    const isNew = newMode === "new";
    if (!isNew && !apptForm.patient_id) { toast.error("Selecciona un paciente"); return; }
    if (isNew && !newName.trim()) { toast.error("Escribe el nombre del paciente"); return; }
    if (!apptForm.scheduled_at) { toast.error("Elige fecha y hora"); return; }
    setSavingAppt(true);
    try {
      const res = await api.post("/appointments", {
        ...(isNew ? { new_patient_name: newName.trim(), new_patient_phone: newPhone.trim() } : { patient_id: apptForm.patient_id }),
        scheduled_at: new Date(apptForm.scheduled_at).toISOString(),
        reason: apptForm.reason,
        type: newType,
        email: apptForm.email,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      toast.success(newType === "cirugia" ? "Cirugía agendada" : "Consulta agendada", { description: apptForm.email ? `Enviaremos la confirmación a ${apptForm.email}` : undefined });
      if (apptForm.email && !isNew) setPatients((ps) => ps.map((p) => p.patient_id === apptForm.patient_id ? { ...p, email: apptForm.email } : p));
      const created = res.data;
      const patient = patients.find((p) => p.patient_id === apptForm.patient_id);
      setNewType(null);
      load();
      window.dispatchEvent(new CustomEvent("clinped:appointments-updated"));
      if (created.type === "cirugia" && patient) setSurgeryFor({ appointment: created, patient });
    } catch { toast.error("No se pudo agendar"); }
    finally { setSavingAppt(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/appointments").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    loadGoogle();
  };
  const loadGoogle = async () => {
    try {
      const st = await api.get("/calendar/status");
      if (!st.data?.connected) { setGEvents([]); setGError(null); return; }
      const now = new Date();
      const start = new Date(now.getTime() - 2 * 86400000).toISOString();
      const end = new Date(now.getTime() + 90 * 86400000).toISOString();
      const r = await api.get(`/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      const data = r.data;
      const evs = Array.isArray(data) ? data : (data?.events || []);
      setGEvents(evs);
      setGError(Array.isArray(data) ? null : (data?.error || null));
    } catch { setGEvents([]); setGError("read_failed"); }
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("clinped:appointments-updated", h);
    return () => window.removeEventListener("clinped:appointments-updated", h);
  }, []);

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const dayKey = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; } };

  const groups = useMemo(() => {
    const now = new Date();
    // Citas propias de ClinPed
    let list = items.map((a) => ({ ...a, _source: "clinped", _when: a.scheduled_at }));
    // Eventos de Google que NO correspondan a una cita ya sincronizada (evita duplicados)
    const syncedIds = new Set(items.map((a) => a.google_event_id).filter(Boolean));
    const gList = gEvents
      .filter((e) => !syncedIds.has(e.google_event_id))
      .map((e) => ({
        _source: "google", appointment_id: `g-${e.google_event_id}`,
        _when: e.start, scheduled_at: e.start, patient_name: e.summary,
        html_link: e.html_link, all_day: e.all_day, status: "programada",
      }));
    let all = [...list, ...gList];
    if (filter === "proximas") all = all.filter((a) => new Date(a._when) >= new Date(now.getTime() - 3600000) && a.status === "programada");
    all.sort((a, b) => new Date(a._when) - new Date(b._when));
    const g = {};
    for (const a of all) { const k = dayKey(a._when); (g[k] = g[k] || []).push(a); }
    return g;
  }, [items, gEvents, filter]);

  const waHref = (a) => {
    const num = waDigits(a.guardian_phone);
    if (!num) return null;
    const who = a.guardian_name ? `${a.guardian_name}, ` : "";
    const msg = `Hola ${who}le recordamos la cita de ${a.patient_name} con ${user?.name || "su médico"} en ClinPed para el ${dayKey(a.scheduled_at)} a las ${fmtTime(a.scheduled_at)}.` + (a.reason ? ` Motivo: ${a.reason}.` : "") + ` Si necesita reprogramar, por favor avísenos. ¡Gracias!`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const keys = Object.keys(groups);

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-brand-ink flex items-center gap-2"><CalendarDays className="text-brand-sage" /> Agenda</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><Mic size={14} /> Tip: mantén presionado a Oci y di "agenda a [paciente] para el [fecha]".</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="agenda-new-consulta" onClick={() => openNew("seguimiento")}
            className="flex items-center gap-1.5 bg-brand-sage text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition-all">
            <Stethoscope size={16} /> Agendar consulta
          </button>
          <button data-testid="agenda-new-cirugia" onClick={() => openNew("cirugia")}
            className="flex items-center gap-1.5 bg-brand-terracotta text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-terracottaDark transition-all">
            <Scissors size={16} /> Agendar cirugía
          </button>
          <div className="flex bg-muted rounded-full p-1">
            <button data-testid="agenda-filter-proximas" onClick={() => setFilter("proximas")}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${filter === "proximas" ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>Próximas</button>
            <button data-testid="agenda-filter-todas" onClick={() => setFilter("todas")}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${filter === "todas" ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>Todas</button>
          </div>
        </div>
      </div>

      <GoogleCalendarCard />
      <WhatsAppReminders />

      {gError === "reauth" && (
        <div data-testid="gcal-reauth-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <span className="text-amber-600 mt-0.5"><CalendarDays size={18} /></span>
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Reconecta tu Google Calendar</p>
            <p className="text-amber-700">Tu permiso con Google expiró o cambió. Desconéctalo y vuelve a conectarlo en la tarjeta de arriba para volver a ver tus eventos.</p>
          </div>
        </div>
      )}
      {gError === "read_failed" && (
        <div data-testid="gcal-error-banner" className="bg-stone-50 border border-stone-200 rounded-2xl p-3 text-sm text-brand-ink/70">
          No se pudieron leer los eventos de Google en este momento. Intenta recargar en unos minutos.
        </div>
      )}

      {loading ? (
        <div className="h-40 flex items-center justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
      ) : keys.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center text-muted-foreground border border-stone-100" data-testid="agenda-empty">
          No hay citas {filter === "proximas" ? "próximas" : ""}. Agéndalas desde el expediente del paciente o por voz con Oci.
        </div>
      ) : (
        <div className="space-y-6">
          {keys.map((k) => (
            <div key={k} data-testid={`agenda-day-${k}`}>
              <h2 className="font-outfit font-bold text-brand-ink capitalize mb-2.5 text-sm">{k}</h2>
              <div className="space-y-2.5">
                {groups[k].map((a) => {
                  if (a._source === "google") {
                    return (
                      <motion.div key={a.appointment_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        data-testid={`agenda-gevent-${a.appointment_id}`}
                        className="bg-white rounded-2xl border border-dashed border-[#4285F4]/40 p-3.5 flex items-center gap-3 shadow-[0_4px_16px_rgb(61,64,91,0.03)]">
                        <div className="w-14 h-14 rounded-2xl bg-[#4285F4]/10 text-[#4285F4] flex flex-col items-center justify-center shrink-0">
                          <CalendarDays size={15} /><span className="text-xs font-bold mt-0.5">{a.all_day ? "Todo el día" : fmtTime(a._when)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-outfit font-semibold text-brand-ink truncate">{a.patient_name || "(sin título)"}</p>
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#4285F4]/10 text-[#4285F4]">Google Calendar</span>
                        </div>
                        {a.html_link && (
                          <a data-testid={`agenda-gopen-${a.appointment_id}`} href={a.html_link} target="_blank" rel="noopener noreferrer" title="Abrir en Google Calendar"
                            className="flex items-center gap-1 text-[11px] font-bold text-[#4285F4] border border-[#4285F4]/30 hover:bg-[#4285F4]/5 px-2.5 py-1.5 rounded-lg transition-all shrink-0"><ArrowRight size={14} /> Abrir</a>
                        )}
                      </motion.div>
                    );
                  }
                  const st = STATUS[a.status] || STATUS.programada;
                  return (
                    <motion.div key={a.appointment_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      data-testid={`agenda-appt-${a.appointment_id}`}
                      className="bg-white rounded-2xl border border-stone-100 p-3.5 flex items-center gap-3 shadow-[0_4px_16px_rgb(61,64,91,0.03)]">
                      <div className="w-14 h-14 rounded-2xl bg-brand-sage/15 text-brand-sageDark flex flex-col items-center justify-center shrink-0">
                        <Clock size={15} /><span className="text-xs font-bold mt-0.5">{fmtTime(a.scheduled_at)}</span>
                      </div>
                      <button data-testid={`agenda-open-${a.appointment_id}`} onClick={() => a.patient_id ? navigate(`/pacientes/${a.patient_id}?tab=citas`) : setIntakeFor(a.appointment_id)} className="flex-1 min-w-0 text-left">
                        <p className="font-outfit font-semibold text-brand-ink truncate">{a.patient_name}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-sand/40 text-amber-700">{TYPE_LABEL[a.type] || a.type}</span>
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.l}</span>
                          {!a.patient_id && <span data-testid={`agenda-new-badge-${a.appointment_id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-ink text-white flex items-center gap-1"><UserPlus size={11} /> Primera vez</span>}
                          {a.attendance_confirmed_at && <span data-testid={`agenda-confirmed-${a.appointment_id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 flex items-center gap-1"><CheckCircle2 size={11} /> Confirmó</span>}
                          {a.cancelled_by === "paciente" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Canceló el paciente</span>}
                          {a.reason && <span className="text-xs text-muted-foreground truncate">· {a.reason}</span>}
                        </div>
                      </button>
                      {(a.intake_completed_at || !a.patient_id) && (
                        <button data-testid={`agenda-intake-${a.appointment_id}`} onClick={() => setIntakeFor(a.appointment_id)} title="Ver pre-registro"
                          className={`flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-colors shrink-0 ${a.intake_completed_at ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-stone-100 text-brand-ink/60 hover:bg-stone-200"}`}>
                          <ClipboardCheck size={14} /> {a.intake_completed_at ? "Pre-registro" : "Sin pre-registro"}</button>
                      )}
                      {a.guardian_phone && a.status === "programada" && (
                        <a data-testid={`agenda-wa-${a.appointment_id}`} href={waHref(a)} target="_blank" rel="noopener noreferrer" title="Recordar por WhatsApp"
                          className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#25D366] hover:opacity-90 px-2.5 py-1.5 rounded-lg transition-all shrink-0"><MessageCircle size={14} /> WhatsApp</a>
                      )}
                      <ArrowRight size={16} className="text-muted-foreground shrink-0" />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Diálogo: agendar consulta / cirugía */}
      <Dialog open={newType !== null} onOpenChange={(o) => !o && setNewType(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="agenda-new-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink flex items-center gap-2">
            {newType === "cirugia" ? <><Scissors size={18} className="text-brand-terracotta" /> Agendar cirugía</> : <><Stethoscope size={18} className="text-brand-sage" /> Agendar consulta</>}
          </DialogTitle></DialogHeader>
          <div className="space-y-3.5 mt-1">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Paciente</Label>
                <div className="flex bg-muted rounded-full p-0.5">
                  {[["existing", "Con expediente"], ["new", "Primera vez"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`agenda-mode-${v}`} onClick={() => setNewMode(v)}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${newMode === v ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {newMode === "new" ? (
                <div className="space-y-2.5 mt-2" data-testid="agenda-new-patient">
                  <Input data-testid="agenda-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre del paciente" className="rounded-xl" />
                  <Input data-testid="agenda-new-phone" type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Teléfono / WhatsApp" className="rounded-xl" />
                  <p className="text-[11px] text-muted-foreground">El paciente llenará su ficha de identificación en el pre-registro y con ella crearás su expediente.</p>
                </div>
              ) : (<>
              <div className="relative mt-1.5">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="agenda-patient-search" value={pQuery} onChange={(e) => setPQuery(e.target.value)} placeholder="Buscar paciente…" className="rounded-xl pl-8" />
              </div>
              <div className="mt-2 max-h-44 overflow-y-auto space-y-1" data-testid="agenda-patient-list">
                {patients.filter((p) => !pQuery || p.name.toLowerCase().includes(pQuery.toLowerCase()) || (p.record_number || "").toLowerCase().includes(pQuery.toLowerCase())).slice(0, 30).map((p) => (
                  <button key={p.patient_id} type="button" data-testid={`agenda-patient-${p.patient_id}`}
                    onClick={() => setApptForm((f) => ({ ...f, patient_id: p.patient_id, email: p.email || "" }))}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all ${apptForm.patient_id === p.patient_id ? "bg-brand-terracotta text-white" : "bg-stone-50 hover:bg-stone-100 text-brand-ink"}`}>
                    <span className="font-semibold">{p.name}</span> {p.record_number ? <span className="opacity-70">· {p.record_number}</span> : null}
                  </button>
                ))}
                {patients.length === 0 && <p className="text-xs text-muted-foreground px-1 py-2">No hay pacientes. Regístralos primero.</p>}
              </div>
              </>)}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Fecha y hora</Label>
              <Input data-testid="agenda-datetime" type="datetime-local" value={apptForm.scheduled_at} onChange={(e) => setApptForm((f) => ({ ...f, scheduled_at: e.target.value }))} className="rounded-xl mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">{newType === "cirugia" ? "Procedimiento / motivo" : "Motivo"}</Label>
              <Input data-testid="agenda-reason" value={apptForm.reason} onChange={(e) => setApptForm((f) => ({ ...f, reason: e.target.value }))} className="rounded-xl mt-1.5" placeholder={newType === "cirugia" ? "Ej. Circuncisión" : "Ej. Control de niño sano"} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Correo del paciente o tutor</Label>
              <Input data-testid="agenda-email" type="email" value={apptForm.email} onChange={(e) => setApptForm((f) => ({ ...f, email: e.target.value }))} className="rounded-xl mt-1.5" placeholder="Recibirá confirmación y recordatorio 24 h antes" />
            </div>
            <button data-testid="agenda-save-appt" onClick={saveNew} disabled={savingAppt}
              className="w-full flex items-center justify-center gap-2 bg-brand-ink hover:opacity-90 text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-60">
              {savingAppt ? <Loader2 size={16} className="animate-spin" /> : <CalendarPlus size={16} />} {newType === "cirugia" ? "Agendar cirugía" : "Agendar consulta"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <IntakeDialog appointmentId={intakeFor} onClose={() => setIntakeFor(null)} />

      {/* Diálogo: notas quirúrgicas tras agendar cirugía */}
      <Dialog open={!!surgeryFor} onOpenChange={(o) => !o && setSurgeryFor(null)}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agenda-surgery-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink flex items-center gap-2"><Scissors size={18} className="text-brand-terracotta" /> Notas quirúrgicas — {surgeryFor?.patient?.name}</DialogTitle></DialogHeader>
          {surgeryFor && <SurgeryNotes appointment={surgeryFor.appointment} patient={surgeryFor.patient} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
